import {
  CARRIER_POSTAL_CODE_MATCH_KIND,
  CARRIER_TEXT_MATCH_KIND,
  carrierPostalCodeMatchKindSchema,
  carrierTextMatchKindSchema,
  countryCodeSchema,
  ORDER_CHANNELS,
  PENNYLANE_ORIGIN,
  channelLabel,
  statusEntries,
  type CarrierRule,
  type CarrierRuleInput,
} from '@flux/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useId, type JSX } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { Button } from '@/components/ui/button.js';
import { Checkbox } from '@/components/ui/checkbox.js';
import { Field } from '@/components/ui/field.js';
import { Input } from '@/components/ui/input.js';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet.js';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select.js';
import { Switch } from '@/components/ui/switch.js';
import { useStoresList } from '@/features/stores/use-stores-list.js';
import { queryKeys } from '@/lib/query/keys.js';
import { toast } from '@/lib/toast.js';
import { createCarrierRule, updateCarrierRule } from './api.js';
import { useShippingMethods } from './use-shipping-methods.js';

/**
 * Création / édition d'une règle transporteur (`02` §9) dans un **tiroir** —
 * plus de pop-up (décision du 10/09/2026, `README-shell.md`).
 *
 * Schéma local aplati, dérivé de `carrierRuleInputSchema` (`@flux/shared`) :
 * chaque condition facultative est éclatée en un interrupteur « activée » et
 * ses champs, et les pays sont saisis en texte libre séparé par des virgules
 * (pas de multi-sélection dans le kit UI). La conversion vers la forme
 * imbriquée du DTO se fait à la soumission.
 */
const ruleFormSchema = z
  .object({
    name: z.string().min(1, 'Libellé requis.'),
    origins: z.array(z.string().min(1)),
    shippingMethodEnabled: z.boolean(),
    shippingMethodMatch: carrierTextMatchKindSchema,
    shippingMethodValue: z.string(),
    countriesRaw: z.string(),
    postalCodeEnabled: z.boolean(),
    postalCodeMatch: carrierPostalCodeMatchKindSchema,
    postalCodeValue: z.string(),
    channels: z.array(z.string().min(1)),
    code: z.string().min(1, 'Code Martelet requis.'),
    label: z.string().min(1, 'Libellé Martelet requis.'),
    pickup: z.boolean(),
    countryOverride: z.string(),
  })
  .superRefine((values, ctx) => {
    if (values.shippingMethodEnabled && values.shippingMethodValue.trim() === '') {
      ctx.addIssue({ code: 'custom', path: ['shippingMethodValue'], message: 'Valeur requise.' });
    }
    if (values.postalCodeEnabled && values.postalCodeValue.trim() === '') {
      ctx.addIssue({ code: 'custom', path: ['postalCodeValue'], message: 'Valeur requise.' });
    }
    if (parseCountriesRaw(values.countriesRaw) === null) {
      ctx.addIssue({
        code: 'custom',
        path: ['countriesRaw'],
        message: 'Codes pays ISO-2 séparés par des virgules (ex. FR, BE, LU).',
      });
    }
    if (
      values.countryOverride !== '' &&
      !countryCodeSchema.safeParse(values.countryOverride).success
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['countryOverride'],
        message: 'Code pays ISO-2 (ex. FR).',
      });
    }
  });

type RuleFormValues = z.infer<typeof ruleFormSchema>;

const countriesArraySchema = z.array(countryCodeSchema);

/** `null` = saisie invalide ; `[]` = aucune contrainte de pays. */
function parseCountriesRaw(raw: string): string[] | null {
  const countries = raw
    .split(',')
    .map((entry) => entry.trim().toUpperCase())
    .filter((entry) => entry.length > 0);
  const parsed = countriesArraySchema.safeParse(countries);
  return parsed.success ? parsed.data : null;
}

function defaultValuesFor(rule: CarrierRule | null): RuleFormValues {
  if (!rule) {
    return {
      name: '',
      origins: [],
      shippingMethodEnabled: false,
      shippingMethodMatch: 'contains',
      shippingMethodValue: '',
      countriesRaw: '',
      postalCodeEnabled: false,
      postalCodeMatch: 'startsWith',
      postalCodeValue: '',
      channels: [],
      code: '',
      label: '',
      pickup: false,
      countryOverride: '',
    };
  }
  return {
    name: rule.name,
    origins: [...rule.conditions.origins],
    shippingMethodEnabled: rule.conditions.shippingMethod !== null,
    shippingMethodMatch: rule.conditions.shippingMethod?.match ?? 'contains',
    shippingMethodValue: rule.conditions.shippingMethod?.value ?? '',
    countriesRaw: rule.conditions.countries.join(', '),
    postalCodeEnabled: rule.conditions.postalCode !== null,
    postalCodeMatch: rule.conditions.postalCode?.match ?? 'startsWith',
    postalCodeValue: rule.conditions.postalCode?.value ?? '',
    channels: [...rule.conditions.channels],
    code: rule.result.code,
    label: rule.result.label,
    pickup: rule.result.pickup,
    countryOverride: rule.result.countryOverride ?? '',
  };
}

function toInput(values: RuleFormValues): CarrierRuleInput {
  return {
    name: values.name.trim(),
    conditions: {
      origins: values.origins,
      shippingMethod: values.shippingMethodEnabled
        ? { match: values.shippingMethodMatch, value: values.shippingMethodValue.trim() }
        : null,
      countries: parseCountriesRaw(values.countriesRaw) ?? [],
      postalCode: values.postalCodeEnabled
        ? { match: values.postalCodeMatch, value: values.postalCodeValue.trim() }
        : null,
      channels: values.channels,
    },
    result: {
      code: values.code.trim(),
      label: values.label.trim(),
      pickup: values.pickup,
      countryOverride: values.countryOverride === '' ? null : values.countryOverride,
    },
  };
}

/** Bascule une valeur dans un tableau de cases à cocher. */
function toggle(list: readonly string[], value: string, checked: boolean): string[] {
  return checked ? [...list, value] : list.filter((entry) => entry !== value);
}

export interface CarrierRuleSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** `null` = création ; sinon édition de cette règle. */
  rule: CarrierRule | null;
  /** Boutique dont les modes de livraison alimentent les suggestions. */
  suggestionsStore?: string | undefined;
  inline?: boolean | undefined;
}

export function CarrierRuleSheet({
  open,
  onOpenChange,
  rule,
  suggestionsStore,
  inline = false,
}: CarrierRuleSheetProps): JSX.Element {
  const queryClient = useQueryClient();
  const isEdit = rule !== null;
  const stores = useStoresList();
  const shippingMethods = useShippingMethods(suggestionsStore);
  const methodListId = useId();
  const conditionsTitleId = useId();
  const resultTitleId = useId();

  const {
    control,
    register,
    handleSubmit,
    watch,
    formState: { errors, isDirty },
  } = useForm<RuleFormValues>({
    resolver: zodResolver(ruleFormSchema),
    values: defaultValuesFor(rule),
  });

  const shippingMethodEnabled = watch('shippingMethodEnabled');
  const postalCodeEnabled = watch('postalCodeEnabled');

  const mutation = useMutation({
    mutationFn: (input: CarrierRuleInput) =>
      isEdit && rule ? updateCarrierRule(rule.id, input) : createCarrierRule(input),
    onSuccess: () => {
      toast.success(isEdit ? 'Règle transporteur mise à jour.' : 'Règle transporteur créée.');
      void queryClient.invalidateQueries({ queryKey: queryKeys.carriers.all });
      onOpenChange(false);
    },
    onError: (error: unknown) => {
      toast.fromError(error);
    },
  });

  const originOptions = [
    ...(stores.data?.items ?? []).map((store) => ({ code: store.code, label: store.name })),
    { code: PENNYLANE_ORIGIN, label: 'Pennylane' },
  ];

  const content = (
    <>
        {inline ? <div><h2 className="text-xl font-semibold">{isEdit ? 'Modifier la règle' : 'Nouvelle règle transporteur'}</h2><p className="text-sm text-muted-foreground">Les règles sont évaluées de haut en bas. Une condition vide ne contraint rien.</p></div> : <SheetHeader>
          <SheetTitle>{isEdit ? 'Modifier la règle' : 'Nouvelle règle transporteur'}</SheetTitle>
          <SheetDescription>
            Les règles sont évaluées de haut en bas : la première dont toutes les conditions sont
            satisfaites l’emporte. Une condition laissée vide ne contraint rien.
          </SheetDescription>
        </SheetHeader>}

        <form
          className="flex flex-col gap-6"
          onSubmit={(event) => {
            void handleSubmit((values) => mutation.mutateAsync(toInput(values)))(event);
          }}
        >
          <Field
            label="Libellé de la règle"
            htmlFor="carrier-rule-name"
            error={errors.name?.message}
          >
            <Input
              id="carrier-rule-name"
              placeholder="Corse — Chronopost"
              aria-invalid={errors.name ? true : undefined}
              {...register('name')}
            />
          </Field>

          <div
            role="group"
            aria-labelledby={conditionsTitleId}
            className="flex flex-col gap-5 border-t border-border pt-5"
          >
            <h3 id={conditionsTitleId} className="text-sm font-semibold">
              Conditions
            </h3>

            <div className="flex flex-col gap-2">
              <div className="flex flex-col gap-0.5">
                <span className="text-sm font-medium">Origine</span>
                <p className="text-xs text-muted-foreground">
                  Aucune case cochée = toutes les origines.
                </p>
              </div>
              <Controller
                control={control}
                name="origins"
                render={({ field }) => (
                  <div className="flex flex-wrap gap-x-4 gap-y-2">
                    {originOptions.map((option) => (
                      <label key={option.code} className="flex items-center gap-2 text-sm">
                        <Checkbox
                          checked={field.value.includes(option.code)}
                          onCheckedChange={(checked) => {
                            field.onChange(toggle(field.value, option.code, checked === true));
                          }}
                        />
                        {option.label}
                      </label>
                    ))}
                  </div>
                )}
              />
            </div>

            <div className="flex flex-col gap-2">
              <Controller
                control={control}
                name="shippingMethodEnabled"
                render={({ field }) => (
                  <label className="flex w-fit items-center gap-2 text-sm font-medium">
                    <Switch
                      checked={field.value}
                      onCheckedChange={(checked) => {
                        field.onChange(checked);
                      }}
                    />
                    Mode de livraison
                  </label>
                )}
              />
              {shippingMethodEnabled ? (
                <div className="flex flex-col gap-2 sm:flex-row sm:items-start">
                  <Controller
                    control={control}
                    name="shippingMethodMatch"
                    render={({ field }) => (
                      <Select
                        value={field.value}
                        onValueChange={(next) => {
                          field.onChange(next);
                        }}
                      >
                        <SelectTrigger
                          className="sm:w-44"
                          aria-label="Comparaison du mode de livraison"
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {statusEntries(CARRIER_TEXT_MATCH_KIND).map((entry) => (
                            <SelectItem key={entry.code} value={entry.code}>
                              {entry.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  />
                  <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                    <Input
                      aria-label="Mode de livraison"
                      list={methodListId}
                      placeholder="Chronopost 18H"
                      aria-invalid={errors.shippingMethodValue ? true : undefined}
                      {...register('shippingMethodValue')}
                    />
                    {/* Suggestions issues des profils d'expédition Shopify de la boutique choisie. */}
                    <datalist id={methodListId}>
                      {shippingMethods.map((method) => (
                        <option key={method} value={method} />
                      ))}
                    </datalist>
                    {errors.shippingMethodValue ? (
                      <p className="text-xs text-status-red-fg">
                        {errors.shippingMethodValue.message}
                      </p>
                    ) : null}
                  </div>
                </div>
              ) : null}
            </div>

            <Field
              label="Pays"
              htmlFor="carrier-rule-countries"
              hint="Vide = tous les pays."
              error={errors.countriesRaw?.message}
            >
              <Input
                id="carrier-rule-countries"
                placeholder="FR, BE, LU"
                aria-invalid={errors.countriesRaw ? true : undefined}
                {...register('countriesRaw')}
              />
            </Field>

            <div className="flex flex-col gap-2">
              <Controller
                control={control}
                name="postalCodeEnabled"
                render={({ field }) => (
                  <label className="flex w-fit items-center gap-2 text-sm font-medium">
                    <Switch
                      checked={field.value}
                      onCheckedChange={(checked) => {
                        field.onChange(checked);
                      }}
                    />
                    Code postal
                  </label>
                )}
              />
              {postalCodeEnabled ? (
                <div className="flex flex-col gap-2 sm:flex-row sm:items-start">
                  <Controller
                    control={control}
                    name="postalCodeMatch"
                    render={({ field }) => (
                      <Select
                        value={field.value}
                        onValueChange={(next) => {
                          field.onChange(next);
                        }}
                      >
                        <SelectTrigger className="sm:w-44" aria-label="Comparaison du code postal">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {statusEntries(CARRIER_POSTAL_CODE_MATCH_KIND).map((entry) => (
                            <SelectItem key={entry.code} value={entry.code}>
                              {entry.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  />
                  <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                    <Input
                      aria-label="Code postal"
                      placeholder="20"
                      aria-invalid={errors.postalCodeValue ? true : undefined}
                      {...register('postalCodeValue')}
                    />
                    {errors.postalCodeValue ? (
                      <p className="text-xs text-status-red-fg">{errors.postalCodeValue.message}</p>
                    ) : null}
                  </div>
                </div>
              ) : null}
            </div>

            <div className="flex flex-col gap-2">
              <div className="flex flex-col gap-0.5">
                <span className="text-sm font-medium">Canal</span>
                <p className="text-xs text-muted-foreground">
                  Aucune case cochée = tous les canaux.
                </p>
              </div>
              <Controller
                control={control}
                name="channels"
                render={({ field }) => (
                  <div className="flex flex-wrap gap-x-4 gap-y-2">
                    {ORDER_CHANNELS.map((code) => (
                      <label key={code} className="flex items-center gap-2 text-sm">
                        <Checkbox
                          checked={field.value.includes(code)}
                          onCheckedChange={(checked) => {
                            field.onChange(toggle(field.value, code, checked === true));
                          }}
                        />
                        {channelLabel(code)}
                      </label>
                    ))}
                  </div>
                )}
              />
            </div>
          </div>

          <div
            role="group"
            aria-labelledby={resultTitleId}
            className="flex flex-col gap-5 border-t border-border pt-5"
          >
            <h3 id={resultTitleId} className="text-sm font-semibold">
              Résultat
            </h3>

            <Field label="Code Martelet" htmlFor="carrier-rule-code" error={errors.code?.message}>
              <Input
                id="carrier-rule-code"
                placeholder="COL_FR_BAL"
                aria-invalid={errors.code ? true : undefined}
                {...register('code')}
              />
            </Field>

            <Field
              label="Libellé Martelet"
              htmlFor="carrier-rule-label"
              error={errors.label?.message}
            >
              <Input
                id="carrier-rule-label"
                placeholder="France Métropolitaine Standard"
                aria-invalid={errors.label ? true : undefined}
                {...register('label')}
              />
            </Field>

            <Controller
              control={control}
              name="pickup"
              render={({ field }) => (
                <label className="flex w-fit items-center gap-2 text-sm font-medium">
                  <Switch
                    checked={field.value}
                    onCheckedChange={(checked) => {
                      field.onChange(checked);
                    }}
                  />
                  Point relais (le code relais devient obligatoire)
                </label>
              )}
            />

            <Field
              label="Pays destinataire forcé"
              htmlFor="carrier-rule-country-override"
              hint="Vide = pays inchangé. Sert à Monaco, que Martelet attend en FR."
              error={errors.countryOverride?.message}
            >
              <Input
                id="carrier-rule-country-override"
                placeholder="FR"
                aria-invalid={errors.countryOverride ? true : undefined}
                {...register('countryOverride')}
              />
            </Field>
          </div>

          <SheetFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                onOpenChange(false);
              }}
            >
              Annuler
            </Button>
            <Button type="submit" isLoading={mutation.isPending} disabled={isEdit && !isDirty}>
              {isEdit ? 'Enregistrer' : 'Créer la règle'}
            </Button>
          </SheetFooter>
        </form>
    </>
  );
  return inline ? <div className="flex flex-col gap-6 rounded-lg border border-border bg-card p-6">{content}</div> : <Sheet open={open} onOpenChange={onOpenChange}><SheetContent className="sm:max-w-lg">{content}</SheetContent></Sheet>;
}
